import {Button, Col, DatePicker, Form, Alert, Modal, notification, Row, Select} from "antd";
import React, {useState} from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import TextArea from "antd/lib/input/TextArea";
import EmployeeSelect from "../Employees/EmployeeSelect";
import moment from "moment/moment";

const gutter = [8, 0];
const MAX_CHARACTERS = 15000;
const MIN_CHARACTERS = 10;
const communication_cases = ['phone', 'email'];
const communication_types = ["other"];

/**
 * @component
 * @alias PartnerAddCommunication
 * @prop {function} addCommunication - function that sends post request to api
 */

const PartnerAddCommunication = ({ id, onUpdate }) => {
  const { t } = useTranslation();

  const [isModalOpen, setModalOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState(false);
  const [form] = Form.useForm();

  const addCommunication = (values) => {

    if (values.to === "other") {
      values.to = null;
    }

    coreApi
      .post("/emails/partner/new", values)
      .then((response) => {
        onUpdate();
      })
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      });
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    setError(newText.length > MAX_CHARACTERS);
    form.setFieldsValue({ body_html: newText });
  };

  return (
    <React.Fragment>
      <Button type="primary" onClick={() => { setModalOpen(true);}}>
        {t("emails.communication_add")}
      </Button>
      <Modal
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setText('');
        }}
        onOk={() => {
          form.submit();
        }}
        okText={t("common.add")}
        open={isModalOpen}
        title={t("emails.communication_add_title")}
      >
        <Form
          form={form}
          onFinish={async (values) => {
            await addCommunication(values);
            form.resetFields();
            setText('');
            setModalOpen(false);
          }}
          layout={"horizontal"}
        >
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name={"created_at"}
                label={t("emails.date")}
                initialValue={moment()}
                rules={[{required: true}]}
              >
                <DatePicker value={moment()}/>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="case_tag_ids"
                label={t("emails.communication")}
                rules={[{ required: true, message: t("emails.communication_required") }]}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={t("emails.communication_placeholder")}
                >
                  {communication_cases.map((type) => (
                    <Select.Option value={type} key={type}>
                      {t("emails.communication_cases." + type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="type_tag_ids"
                label={t("emails.communication_type")}
                rules={[{ required: true, message: t("emails.communication_type_required") }]}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={t("emails.communication_type_placeholder")}
                >
                  {communication_types.map((type) => (
                    <Select.Option key={type} value={type}>
                      {t("emails.types." + type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name="body_html"
                label={t("emails.description")}
                rules={[
                  { required: true, message: t("emails.description_required") },
                  { min: MIN_CHARACTERS, message: t("emails.description_min_length") }
                ]}
                validateStatus={error ? 'error' : ''}
                help={
                  error && (
                    <Alert
                      message={t("emails.communication_text_error")}
                      type="error"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  )
                }
              >
                <TextArea
                  rows={4}
                  value={text}
                  onChange={handleTextChange}
                  showCount={{
                    formatter: ({ count }) => {
                      if (count > 0 && count < MIN_CHARACTERS) {
                        return `${count}/${MAX_CHARACTERS} ⚠️ ${t('emails.description_remaining')}`;
                      }
                      return `${count}/${MAX_CHARACTERS}`;
                    },
                  }}
                  style={{
                    borderColor: text.length > 0 && text.length < MIN_CHARACTERS ? '#ff4d4f' : undefined
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <EmployeeSelect
                required={true}
                name={"from"}
                label={t("common.user")}
                activeUsers={true}
                placeholder={''}
                initialValue={JSON.parse(localStorage.getItem("user")).id}
              />
            </Col>
          </Row>
          <Form.Item name="partner_id" initialValue={id} hidden>
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default PartnerAddCommunication;
